import React, { useState } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faChevronDown,
    faChevronUp,
    faDownload,
    faEnvelope,
    faEnvelopeOpen,
    faPaperclip,
    faTrashCan,
    faCircleDot,
} from '@fortawesome/free-solid-svg-icons'
import { ProcessCardProps } from './Processes'
import { setNotificationsCounts } from '../../features/security/security.slice'
import { useAppDispatch } from '../../store/hooks'
import { api } from '../../auth'

dayjs.extend(relativeTime)

const NotificationCard: React.FC<ProcessCardProps> = ({
    item,
    type,
    onNotificationUpdate,
    onNotificationRemove,
}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isRead, setIsRead] = useState(Boolean(item.is_read))
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isRemoved, setIsRemoved] = useState(false)

    const dispatch = useAppDispatch()
    const axiosPrivate = api

    if (type !== 'notification') return null

    const updateCounts = (res: { data?: { data?: { total_count?: number; unread_count?: number } } }) => {
        const data = res?.data?.data
        if (data != null) {
            dispatch(
                setNotificationsCounts({
                    totalCount: data.total_count ?? 0,
                    unreadCount: data.unread_count ?? 0,
                })
            )
        }
    }

    const markAsRead = () => {
        if (isRead) return
        setIsSubmitting(true)
        axiosPrivate
            .post(`/processes/notifications/${item.id}`, {}, { params: { read: true } })
            .then((res) => {
                setIsRead(true)
                updateCounts(res)
                onNotificationUpdate?.(item.id, { is_read: true })
            })
            .catch(() => {})
            .finally(() => setIsSubmitting(false))
    }

    const markAsUnread = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!isRead) return
        setIsSubmitting(true)
        axiosPrivate
            .post(`/processes/notifications/${item.id}`, {}, { params: { read: false } })
            .then((res) => {
                setIsRead(false)
                updateCounts(res)
                onNotificationUpdate?.(item.id, { is_read: false })
            })
            .catch(() => {})
            .finally(() => setIsSubmitting(false))
    }

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (isDeleting) return
        setIsDeleting(true)
        axiosPrivate
            .delete(`/processes/notifications/${item.id}`)
            .then((res) => {
                updateCounts(res)
                onNotificationRemove?.(item.id)
                setIsRemoved(true)
            })
            .catch(() => {})
            .finally(() => setIsDeleting(false))
    }

    const handleToggleExpand = () => {
        if (isExpanded) {
            // Collapsing: mark as read when user closes the expanded view (so they had time to read it)
            setIsExpanded(false)
            if (!isRead && !isSubmitting) markAsRead()
        } else {
            // Expanding: just open; don't mark as read yet so it stays visible in the Unread tab
            setIsExpanded(true)
        }
    }

    const handleDownloadFile = async (e: React.MouseEvent, fileId: string | number, extension: string) => {
        e.stopPropagation()
        try {
            const response = await axiosPrivate.get(`/dashboard/report/download/${fileId}`, {
                responseType: 'blob',
            })
            const blob = new Blob([response.data])
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = String(fileId)
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            a.remove()
        } catch {
            console.error('Failed to download file')
        }
    }

    if (isRemoved) return null

    const hasFiles = item.files && item.files.length > 0
    const timeAgo = item.created_at ? dayjs(item.created_at).fromNow() : ''
    const timeFull = item.created_at ? dayjs(item.created_at).format('DD MMM YYYY, HH:mm') : ''

    return (
        <article
            className={`notification-card ${isRead ? 'notification-card--read' : 'notification-card--unread'} ${isExpanded ? 'notification-card--expanded' : ''}`}
            data-notification-id={item.id}
        >
            <div className="notification-card__main" onClick={handleToggleExpand} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleToggleExpand()}>
                <div className="notification-card__indicator" title={isRead ? 'Read' : 'Unread'} aria-hidden>
                    {isRead ? (
                        <FontAwesomeIcon icon={faCircleDot} className="notification-card__dot notification-card__dot--read" />
                    ) : (
                        <span className="notification-card__dot notification-card__dot--unread" />
                    )}
                </div>
                <div className="notification-card__icon">
                    <FontAwesomeIcon icon={isRead ? faEnvelopeOpen : faEnvelope} />
                </div>
                <div className="notification-card__content">
                    <p className="notification-card__label">{item.label}</p>
                    <div className="notification-card__meta">
                        <time className="notification-card__time" title={timeFull}>
                            {timeAgo}
                        </time>
                        {hasFiles && (
                            <span className="notification-card__files-badge">
                                <FontAwesomeIcon icon={faPaperclip} />
                                {item.files!.length}
                            </span>
                        )}
                    </div>
                </div>
                <div className="notification-card__actions" onClick={(e) => e.stopPropagation()}>
                    {isRead ? (
                        <button
                            type="button"
                            className="notification-card__action notification-card__action--unread"
                            onClick={markAsUnread}
                            disabled={isSubmitting}
                            title="Mark as unread"
                            aria-label="Mark as unread"
                        >
                            <FontAwesomeIcon icon={faEnvelope} />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="notification-card__action notification-card__action--delete"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        title="Delete"
                        aria-label="Delete notification"
                    >
                        <FontAwesomeIcon icon={faTrashCan} spin={isDeleting} />
                    </button>
                    <span className="notification-card__expand-icon">
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </span>
                </div>
            </div>
            {isExpanded && (
                <div className="notification-card__body">
                    {item.description && <p className="notification-card__description">{item.description}</p>}
                    {hasFiles && (
                        <div className="notification-card__files">
                            {item.files!.map((file) => (
                                <button
                                    key={String(file.file_id)}
                                    type="button"
                                    className="notification-card__file-btn"
                                    onClick={(e) => handleDownloadFile(e, file.file_id, file.file_extension)}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    {file.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </article>
    )
}

export default NotificationCard
